import { Pin, Question, Spark } from "@scoach/ui/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { FollowupList } from "../../../meeting/live/components/FollowupList.tsx";
import { HintGroupCard } from "../../../meeting/live/components/HintGroupCard.tsx";
import { groupHints } from "../../../meeting/live/hintGrouping.ts";
import { useLiveMeetingStore } from "../../../meeting/live/store.ts";

const SPLIT_KEY = "sally.interview.coach.split";
const MIN_FRAC = 0.2;
const MAX_FRAC = 0.8;

function loadSplit(): number {
  try {
    const raw = localStorage.getItem(SPLIT_KEY);
    if (raw) {
      const n = Number.parseFloat(raw);
      if (Number.isFinite(n)) return Math.max(MIN_FRAC, Math.min(n, MAX_FRAC));
    }
  } catch {}
  return 0.55;
}

/**
 * Interviewer coaching column: candidate-answer assessments (Strong/Borderline/
 * Weak, with per-answer score + follow-up probes) on top, and suggested
 * follow-up questions below. A draggable divider lets the interviewer choose
 * which half is bigger; the ratio persists across sessions. (Live tips + summary
 * + scorecard now live in the tabbed right panel.)
 */
export function InterviewCoachColumn({ meetingId }: { meetingId: string }) {
  const hints = useLiveMeetingStore((s) => s.hints);
  const pinnedIds = useLiveMeetingStore((s) => s.pinnedHintIds);
  const togglePinned = useLiveMeetingStore((s) => s.togglePinned);
  const followupSets = useLiveMeetingStore((s) => s.followupSets);
  const followupCount = followupSets[followupSets.length - 1]?.items.length ?? 0;
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const [topFrac, setTopFrac] = useState<number>(() =>
    typeof window === "undefined" ? 0.55 : loadSplit(),
  );

  const groups = useMemo(() => groupHints(hints), [hints]);
  const pinnedGroups = useMemo(
    () => groups.filter((g) => g.some((h) => pinnedIds.has(h.id))),
    [groups, pinnedIds],
  );
  const unpinnedGroups = useMemo(
    () => groups.filter((g) => !g.some((h) => pinnedIds.has(h.id))),
    [groups, pinnedIds],
  );
  const visibleCount = groups.reduce((a, g) => a + g.length, 0);

  const onDividerDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";
  }, []);

  useEffect(() => {
    function onMove(e: MouseEvent) {
      if (!draggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const frac = (e.clientY - rect.top) / rect.height;
      setTopFrac(Math.max(MIN_FRAC, Math.min(frac, MAX_FRAC)));
    }
    function onUp() {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      try {
        localStorage.setItem(SPLIT_KEY, String(topFrac));
      } catch {}
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [topFrac]);

  return (
    <section
      className="panel coach-panel coach-hero"
      ref={containerRef}
      style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}
    >
      {/* Answer assessments (per-answer score + follow-up probes) */}
      <section
        className="panel"
        style={{ flex: topFrac, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        <div className="panel-head">
          <div className="seg seg-tabs" style={{ pointerEvents: "none", width: "100%" }}>
            <button type="button" className="on">
              <Spark size={14} /> Answer assessments{" "}
              <span className="tab-count">{visibleCount}</span>
            </button>
          </div>
        </div>

        <div className="coach-scroll scroll" ref={scrollRef} style={{ fontSize: 15, flex: 1, minHeight: 0 }}>
          {visibleCount === 0 && (
            <div className="hint-thinking hint-in">
              <div className="ht-row">
                <span className="dot dot-pulse" style={{ background: "#7627bb" }} />
                <span
                  className="dot dot-pulse"
                  style={{ background: "#7627bb", animationDelay: "160ms" }}
                />
                <span
                  className="dot dot-pulse"
                  style={{ background: "#7627bb", animationDelay: "320ms" }}
                />
                <span className="ht-label">
                  Listening — assessments appear as the candidate answers…
                </span>
              </div>
            </div>
          )}
          {unpinnedGroups.map((g) => (
            <HintGroupCard key={g[0]!.id} group={g} meetingId={meetingId} />
          ))}
        </div>

        {pinnedGroups.length > 0 && (
          <div className="coach-pinned-dock" style={{ fontSize: 15 }}>
            <div className="coach-pinned-dock-head">
              <span className="coach-pinned-dock-title">
                <Pin size={12} /> Pinned assessment
              </span>
              <button
                type="button"
                className="coach-pinned-unpin-btn"
                onClick={() => {
                  const firstId = pinnedGroups[0]?.[0]?.id;
                  if (firstId) togglePinned(firstId);
                }}
                title="Unpin"
              >
                ✕ Unpin
              </button>
            </div>
            <div className="coach-pinned-dock-body scroll">
              {pinnedGroups.map((g) => (
                <HintGroupCard key={g[0]!.id} group={g} meetingId={meetingId} />
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Draggable divider — drag up/down to resize assessments vs follow-ups */}
      <div
        className="row-resizer"
        onMouseDown={onDividerDown}
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize assessments / follow-ups"
        title="Drag to resize"
        style={{
          height: 8,
          cursor: "row-resize",
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span style={{ width: 40, height: 3, borderRadius: 2, background: "var(--border-1)" }} />
      </div>

      {/* Suggested follow-up questions */}
      <section
        className="panel coach-hero-followups"
        style={{ flex: 1 - topFrac, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        <div className="panel-head">
          <div className="seg seg-tabs" style={{ pointerEvents: "none", width: "100%" }}>
            <button type="button" className="on">
              <Question size={14} /> Suggested follow-ups{" "}
              <span className="tab-count">{followupCount}</span>
            </button>
          </div>
        </div>
        <div className="fu-scroll" style={{ fontSize: 15 }}>
          <FollowupList />
        </div>
      </section>
    </section>
  );
}
